/**
 * /onboarding/individual — END-CUSTOMER (B2C) 5-step onboarding wrapper (route shell).
 *
 * The whole 5-step journey (provision → subscription → MFA → documents → confirm)
 * lives in `./individual-screen.tsx` and is lazy-loaded via `lazyRouteComponent`,
 * so the reused doc-dump confirm card-stack (+ its heavy editable-field / source
 * preview shells) leaves the entry bundle — `/onboarding/individual` is a
 * deliberate destination, not a first-paint route. Same shell+screen split as
 * `/app/setup` (`routes/app/setup.tsx`), which keeps the size-limit budget held.
 *
 * Hand-added to `routeTree.gen.ts` mirroring the sibling `/onboarding/firm`
 * registration (do NOT regen the routeTree via the plugin — it rewrites the
 * hand-edited custom URLs; see `app/setup.tsx` / `document-review.$jobId.tsx`).
 *
 * NB: this shell deliberately does NOT re-export anything from
 * `./individual-screen` — a static re-export would pull the heavy screen module
 * back into the eager entry bundle, defeating the lazy split. The pure helpers
 * (`individualDisplayName` / `tenantNameFor` / `trackerPosition`) are imported
 * directly from `./individual-screen` by tests instead.
 *
 * ARC-01 (grader, 5 Sep 2026, P0): this wizard is the CLIENT's own. A firm
 * acting for a client (act-as) is never a client setting itself up: the wizard
 * would stop the operator at their own authenticator step and offer a
 * subscription step to a practice-paid client. Under act-as the route hands
 * straight to the firm's structure builder, the same place "Map structure" on
 * the Client Book leads. The check lives here, on the route, so a bookmark or a
 * stale link cannot reach the screen either.
 */

import { createFileRoute, lazyRouteComponent, Navigate } from '@tanstack/react-router';
import { useIsActingAsClient } from '../../lib/acting/context';

const OnboardingIndividualLazy = lazyRouteComponent(
  () => import('./individual-screen'),
  'OnboardingIndividualScreen',
);

export function OnboardingIndividualRoute() {
  const actingAsClient = useIsActingAsClient();
  if (actingAsClient) return <Navigate to="/app/structure" replace />;
  return <OnboardingIndividualLazy />;
}

export const Route = createFileRoute('/onboarding/individual')({
  component: OnboardingIndividualRoute,
});
